/**
 * PropertyModifiers
 *
 * Handles price-modifying cart line item properties:
 *   - select, visual select, boolean, radio
 *   - conditional text inputs and conditional radio groups
 *     triggered when a modifier's value matches one of N configured values
 *     (case-insensitive; empty list = trigger on any value / checked state)
 *   - modifier-level show/hide conditioned on another modifier's value
 *   - accordion groups (handled by native <details>)
 *   - aggregated _total_price_modifier hidden input for Shopify Function
 *
 * Data is read from a <script type="application/json" data-modifiers-data>
 * block rendered by property-modifiers.liquid.
 *
 * TODO: Verify the variant change event name against the new theme's
 *       product-info.js — currently listening for 'variant:changed'.
 */

class PropertyModifiers {
  constructor(container) {
    this.container        = container;
    this.form             = null;
    this.data             = null;
    this.currentValues    = {}; // { property_name: { value, priceModifier } }
    this.shipTimeData     = {}; // { property_name: { min, max } } — from modifiers data
    this.touchedFields = new Set(); // tracks which fields the user has touched (blurred)

    // Presentment currency (Shopify Markets). `rate` converts the shop's base
    // (USD) price_modifier values into the buyer's currency for DISPLAY only.
    // The submitted _total_price_modifier stays in USD — the Shopify cart
    // transform function converts it at checkout.
    const shopifyCurrency = (typeof window !== 'undefined' && window.Shopify && window.Shopify.currency) || null;
    const parsedRate      = shopifyCurrency ? parseFloat(shopifyCurrency.rate) : NaN;
    this.presentmentRate     = !isNaN(parsedRate) && parsedRate > 0 ? parsedRate : 1;
    this.presentmentCurrency = shopifyCurrency && shopifyCurrency.active ? shopifyCurrency.active : null;

    this._init();
  }

  // ─── Init ──────────────────────────────────────────────────────────────────

  _init() {
    const raw = this.container.querySelector('[data-modifiers-data]');
    if (!raw) return;

    try {
      this.data = JSON.parse(raw.textContent);
    } catch (e) {
      console.error('[PropertyModifiers] Failed to parse modifiers data', e);
      return;
    }

    // Locate the product form by the form attribute on any modifier input.
    // Must find a modifier-specific input (not the hidden variant id input) to get the right form id.
    const modifierFormAttr = this.container.querySelector('select[data-property-type], input[data-property-type], [data-total-price-modifier]')?.getAttribute('form');
    this.form = modifierFormAttr ? document.getElementById(modifierFormAttr) : this.container.closest('form');

    // Initialise currentValues and shipTimeData from data
    this.data.modifiers.forEach(modifier => {
      this.currentValues[modifier.property_name] = {
        value:         '',
        priceModifier: 0,
      };
      this.shipTimeData[modifier.property_name] = {
        min:         modifier.ship_time_min || 0,
        max:         modifier.ship_time_max || 0,
        isExclusive: modifier.is_exclusive || false,
      };
    });

    this._bindSelectInputs();
    this._bindVisualSelectors();
    this._bindBooleanInputs();
    this._bindRadioInputs();
    this._bindConditionalTextInputs();
    this._renderOptionPrices();
    this._evaluateAllModifierVisibility();
    this._bindFormValidation();
    this._bindVariantChange();
    this._bindQuantityChange();

    // Sync name attributes on init so never_in_stock / never_returnable
    // products submit the right properties even before any user interaction.
    this._syncSystemInputNames(
      false,
      0,
      0,
      this.data.neverReturnable
    );

    // Establish oversold-aware ship time on load — covers a product that
    // already has units in cart, or a pre-filled quantity that exceeds stock,
    // before the user touches anything.
    this._updateShipTime();
  }

  // ─── Input binding ─────────────────────────────────────────────────────────

  _bindSelectInputs() {
    this.container
      .querySelectorAll('select[data-property-type="select"]:not([data-visual-select])')
      .forEach(select => {
        select.addEventListener('change', () => {
          const name          = select.dataset.propertyName;
          const selected      = select.options[select.selectedIndex];
          const priceModifier = parseFloat(selected?.dataset.priceModifier || 0);

          this.currentValues[name] = {
            value:         select.value,
            priceModifier: priceModifier,
          };

          this._syncSelectName(select);
          this._evaluateConditionalFields(name);
          this._evaluateAllModifierVisibility();
          this._applyExclusiveLock();
          this._updateTotalPrice();
        });

        // Sync on init — handles back-navigation or pre-filled values
        this._syncSelectName(select);
      });
  }

  // Remove name when value is empty so unselected selects never submit a blank
  // property to the order. Restores name when a value is chosen.
  _syncSelectName(select) {
    const propertyName = select.dataset.propertyName;
    if (!propertyName) return;
    if (select.value && select.value !== '') {
      select.setAttribute('name', `properties[${propertyName}]`);
    } else {
      select.removeAttribute('name');
    }
  }

  _bindVisualSelectors() {
    // ── Trigger buttons — open panel ──────────────────────────────────────
    this.container
      .querySelectorAll('[data-visual-trigger]')
      .forEach(trigger => {
        trigger.addEventListener('click', () => {
          const propertyName = trigger.dataset.visualTrigger;
          this._openVisualPanel(propertyName);
        });
      });

    // ── Close buttons — close panel without changing selection ─────────────
    this.container
      .querySelectorAll('[data-visual-panel-close]')
      .forEach(btn => {
        btn.addEventListener('click', () => {
          this._closeVisualPanel(btn.dataset.visualPanelClose);
        });
      });

    // ── Tile clicks — select and close ────────────────────────────────────
    this.container
      .querySelectorAll('[data-visual-selector-for]')
      .forEach(grid => {
        const propertyName = grid.dataset.visualSelectorFor;
        const hiddenSelect = this.container.querySelector(
          `select[data-visual-select][data-property-name="${propertyName}"]`
        );
        if (!hiddenSelect) return;

        // Sync name on init
        this._syncSelectName(hiddenSelect);

        grid.querySelectorAll('.visual-selector__tile').forEach(tile => {
          tile.addEventListener('click', () => {
            // Deselect all tiles
            grid.querySelectorAll('.visual-selector__tile').forEach(t => {
              t.classList.remove('visual-selector__tile--selected');
              t.setAttribute('aria-pressed', 'false');
              t.setAttribute('aria-selected', 'false');
            });

            // Select clicked tile
            tile.classList.add('visual-selector__tile--selected');
            tile.setAttribute('aria-pressed', 'true');
            tile.setAttribute('aria-selected', 'true');

            // Sync hidden select
            const value         = tile.dataset.value;
            hiddenSelect.value  = value;

            const priceModifier = parseFloat(tile.dataset.priceModifier || 0);
            this.currentValues[propertyName] = { value, priceModifier };

            // Sync name attribute so empty selection doesn't submit a blank property
            this._syncSelectName(hiddenSelect);

            // Update trigger label and price
            this._updateVisualTriggerLabel(propertyName, tile);

            // Close the panel
            this._closeVisualPanel(propertyName);

            // Dispatch change so conditional logic picks it up
            hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));

            this._evaluateConditionalFields(propertyName);
            this._evaluateAllModifierVisibility();
            this._applyExclusiveLock();
            this._updateTotalPrice();
          });
        });
      });
  }

  _openVisualPanel(propertyName) {
    const panel   = this.container.querySelector(`[data-visual-panel="${propertyName}"]`);
    const trigger = this.container.querySelector(`[data-visual-trigger="${propertyName}"]`);
    if (!panel) return;

    // Close any other open panels first so they never stack
    this.container.querySelectorAll('[data-visual-panel]').forEach(p => {
      if (p !== panel && p.classList.contains('visual-selector__panel--open')) {
        this._closeVisualPanel(p.dataset.visualPanel);
      }
    });

    panel.setAttribute('aria-hidden', 'false');
    panel.classList.add('visual-selector__panel--open');
    trigger?.setAttribute('aria-expanded', 'true');

    // Outside-click listener — deferred so the triggering click doesn't immediately close it
    this._outsideClickHandler = (e) => {
      if (!panel.contains(e.target) && !trigger.contains(e.target)) {
        this._closeVisualPanel(propertyName);
      }
    };
    setTimeout(() => document.addEventListener('click', this._outsideClickHandler), 0);
  }

  _closeVisualPanel(propertyName) {
    const panel   = this.container.querySelector(`[data-visual-panel="${propertyName}"]`);
    const trigger = this.container.querySelector(`[data-visual-trigger="${propertyName}"]`);
    if (!panel) return;

    panel.setAttribute('aria-hidden', 'true');
    panel.classList.remove('visual-selector__panel--open');
    trigger?.setAttribute('aria-expanded', 'false');

    // Clean up outside-click listener
    if (this._outsideClickHandler) {
      document.removeEventListener('click', this._outsideClickHandler);
      this._outsideClickHandler = null;
    }
  }

  _updateVisualTriggerLabel(propertyName, selectedTile) {
    const labelEl = this.container.querySelector(`[data-trigger-label="${propertyName}"]`);
    const priceEl = this.container.querySelector(`[data-trigger-price="${propertyName}"]`);
    if (!labelEl) return;

    const tileLabel = selectedTile.querySelector('.visual-selector__tile-label');
    // Get just the text node, not the nested price span
    const labelText = [...tileLabel.childNodes]
      .filter(n => n.nodeType === Node.TEXT_NODE)
      .map(n => n.textContent.trim())
      .join('');

    labelEl.textContent = labelText || 'None Selected';

    if (priceEl) {
      const priceModifier = parseFloat(selectedTile.dataset.priceModifier || 0);
      priceEl.textContent = this._formatModifierPrice(priceModifier);
    }
  }

  _bindBooleanInputs() {
    this.container
      .querySelectorAll('input[data-property-type="boolean"]')
      .forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          const name     = checkbox.dataset.propertyName;
          const modifier = this._getModifierData(name);
          let priceModifier = 0;

          if (checkbox.checked && modifier) {
            const trueOption = modifier.options.find(o =>
              ['true', 'yes'].includes(o.value.toLowerCase())
            );
            priceModifier = parseFloat(trueOption?.price_modifier) || 0;
          }

          this.currentValues[name] = {
            value:         checkbox.checked ? 'Yes' : '',
            priceModifier: checkbox.checked ? priceModifier : 0,
          };

          this._evaluateConditionalFields(name);
          this._evaluateAllModifierVisibility();
          this._applyExclusiveLock();
          this._updateTotalPrice();
        });
      });
  }

  _bindRadioInputs() {
    this.container
      .querySelectorAll('input[data-property-type="radio"]')
      .forEach(radio => {
        radio.addEventListener('change', () => {
          if (!radio.checked) return;
          const name = radio.dataset.propertyName;

          this.currentValues[name] = {
            value:         radio.value,
            priceModifier: 0, // radio type has no price impact
          };

          this._evaluateConditionalFields(name);
          this._evaluateAllModifierVisibility();
          // No price update needed for radio type
        });
      });
  }

  _bindConditionalTextInputs() {
    this.container
      .querySelectorAll('input[data-property-type="conditional-text"]')
      .forEach(input => {
        const name = input.dataset.conditionalFor;
        const countEl = this.container.querySelector(`[data-count-for="${name}"]`);

        if (countEl) {
          input.addEventListener('input', () => {
            countEl.textContent = `${input.value.length} / ${input.maxLength} characters`;
          });
        }
      });
  }

  // ─── Conditional field evaluation ─────────────────────────────────────────

  _evaluateConditionalFields(propertyName) {
    const current = this.currentValues[propertyName]?.value ?? '';
    let changed = false;

    // Text input sub-fields
    this.container
      .querySelectorAll(
        `.property-conditional-field[data-conditional-for="${propertyName}"][data-trigger-type="text"]`
      )
      .forEach(wrapper => {
        const triggerValues = this._parseTriggerValues(wrapper.dataset.triggerValues);
        const shouldShow    = this._valueMatchesTriggers(current, triggerValues);
        const isHidden      = wrapper.style.display === 'none';
        if (shouldShow === isHidden) changed = true;
        this._toggleConditionalField(wrapper, shouldShow);
      });

    // Radio sub-fields
    this.container
      .querySelectorAll(
        `.property-conditional-field[data-conditional-for="${propertyName}"][data-trigger-type="radio"]`
      )
      .forEach(wrapper => {
        const triggerValues = this._parseTriggerValues(wrapper.dataset.triggerValues);
        const shouldShow    = this._valueMatchesTriggers(current, triggerValues);
        const isHidden      = wrapper.style.display === 'none';
        if (shouldShow === isHidden) changed = true;
        this._toggleConditionalField(wrapper, shouldShow);
      });

    if (changed) {
      this._updateAtcState();
    }
  }

  _parseTriggerValues(raw) {
    if (!raw || raw.trim() === '') return [];
    return raw.split(',').map(v => v.trim().toLowerCase()).filter(Boolean);
  }

  _valueMatchesTriggers(currentValue, triggerValues) {
    if (!currentValue || currentValue === '') return false;
    if (triggerValues.length === 0) return true;
    return triggerValues.includes(currentValue.toLowerCase());
  }

  _toggleConditionalField(wrapper, show) {
    if (show) {
      wrapper.style.display = '';
      wrapper.querySelectorAll('input, select, textarea').forEach(el => {
        el.disabled = false;
      });
      if (wrapper.dataset.triggerType === 'radio') {
        const firstRadio = wrapper.querySelector('input[type="radio"]');
        if (firstRadio && !wrapper.querySelector('input[type="radio"]:checked')) {
          firstRadio.checked = true;
        }
      }
    } else {
      wrapper.style.display = 'none';
      wrapper.querySelectorAll('input, select, textarea').forEach(el => {
        el.disabled = true;
        if (el.type === 'radio' || el.type === 'checkbox') {
          el.checked = false;
        } else {
          el.value = '';
        }
      });
      const countName = wrapper.dataset.conditionalFor;
      const countEl   = this.container.querySelector(`[data-count-for="${countName}"]`);
      if (countEl) countEl.textContent = `0 / ${wrapper.querySelector('input')?.maxLength} characters`;
    }
  }

  // ─── Modifier-level visibility ─────────────────────────────────────────────

  _evaluateAllModifierVisibility() {
    let changed = false;

    this.container
      .querySelectorAll('.property-modifier-field--conditional')
      .forEach(wrapper => {
        const controlName   = wrapper.dataset.conditionalOn;
        const triggerValues = this._parseTriggerValues(wrapper.dataset.conditionalValues || '');
        const currentVal    = this.currentValues[controlName]?.value || '';
        const shouldShow    = this._valueMatchesTriggers(currentVal, triggerValues);
        const isHidden      = wrapper.style.display === 'none';

        if (shouldShow && isHidden) {
          wrapper.style.display = '';
          this._enableFieldInputs(wrapper);
          changed = true;
        } else if (!shouldShow && !isHidden) {
          wrapper.style.display = 'none';
          this._disableFieldInputs(wrapper);
          changed = true;
        }
      });

    if (changed) {
      this._updateTotalPrice();
      this._updateAtcState();
    }
  }

  _enableFieldInputs(wrapper) {
    wrapper.querySelectorAll('input, select, textarea').forEach(el => {
      if (el.closest('.property-conditional-field[style*="display: none"]')) return;
      el.disabled = false;
      // Restore name on selects based on current value
      if (el.tagName === 'SELECT' && el.dataset.propertyName) {
        this._syncSelectName(el);
      }
    });

    const name = wrapper.dataset.propertyName;
    if (!name) return;

    const select = wrapper.querySelector(`select[data-property-name="${name}"]:not([data-visual-select])`);
    const visualSelect = wrapper.querySelector(`select[data-visual-select][data-property-name="${name}"]`);
    const checkbox = wrapper.querySelector(`input[type="checkbox"][data-property-name="${name}"]`);
    const checkedRadio = wrapper.querySelector(`input[type="radio"][data-property-name="${name}"]:checked`);

    if (select) {
      const selected = select.options[select.selectedIndex];
      this.currentValues[name] = {
        value:         select.value,
        priceModifier: parseFloat(selected?.dataset.priceModifier) || 0,
      };
    } else if (visualSelect) {
      const selected = visualSelect.options[visualSelect.selectedIndex];
      this.currentValues[name] = {
        value:         visualSelect.value,
        priceModifier: parseFloat(selected?.dataset.priceModifier) || 0,
      };
    } else if (checkbox) {
      const modifier = this._getModifierData(name);
      let priceModifier = 0;
      if (checkbox.checked && modifier) {
        const trueOption = modifier.options.find(o =>
          ['true', 'yes'].includes(o.value.toLowerCase())
        );
        priceModifier = trueOption?.price_modifier || 0;
      }
      this.currentValues[name] = { value: checkbox.checked ? 'Yes' : '', priceModifier };
    } else if (checkedRadio) {
      this.currentValues[name] = { value: checkedRadio.value, priceModifier: 0 };
    }
  }

  _disableFieldInputs(wrapper) {
    const propertyName = wrapper.dataset.propertyName;

    if (propertyName && this.currentValues[propertyName]) {
      this.currentValues[propertyName] = { value: '', priceModifier: 0 };
    }

    const priceInput = this.container.querySelector(
      `[data-price-input-for="${propertyName}"]`
    );
    if (priceInput) priceInput.value = '0';

    wrapper.querySelectorAll('input, select, textarea').forEach(el => {
      el.disabled = true;
      if (el.type === 'radio' || el.type === 'checkbox') {
        el.checked = false;
      } else if (el.type !== 'hidden') {
        el.value = '';
      }
      // Remove name from selects so hidden fields don't submit blank properties
      if (el.tagName === 'SELECT' && el.dataset.propertyName) {
        el.removeAttribute('name');
      }
    });

    const grid = wrapper.querySelector('[data-visual-selector-for]');
    if (grid) {
      grid.querySelectorAll('.visual-selector__tile').forEach(t => {
        t.classList.remove('visual-selector__tile--selected');
        t.setAttribute('aria-pressed', 'false');
        t.setAttribute('aria-selected', 'false');
      });
      const noneTile = grid.querySelector('.visual-selector__tile--none');
      if (noneTile) {
        noneTile.classList.add('visual-selector__tile--selected');
        noneTile.setAttribute('aria-pressed', 'true');
        noneTile.setAttribute('aria-selected', 'true');
      }
      const labelEl = wrapper.querySelector(`[data-trigger-label="${propertyName}"]`);
      const priceEl = wrapper.querySelector(`[data-trigger-price="${propertyName}"]`);
      if (labelEl) labelEl.textContent = 'None Selected';
      if (priceEl) priceEl.textContent = '';
      this._closeVisualPanel(propertyName);
    }

    wrapper.querySelectorAll('.property-conditional-field').forEach(cf => {
      this._toggleConditionalField(cf, false);
    });
  }

  // ─── Price calculation ─────────────────────────────────────────────────────

  _updateTotalPrice() {
    const totalModifierDollars = Object.values(this.currentValues)
      .reduce((sum, { priceModifier }) => sum + (parseFloat(priceModifier) || 0), 0);

    // basePrice is already in the buyer's presentment currency (Shopify converts
    // variant.price by market); the USD modifier total must be converted to match.
    const totalCents = this.data.basePrice + Math.round(totalModifierDollars * this.presentmentRate * 100);

    // _total_price_modifier is read by the Shopify Function (always submitted)
    const totalInput = this.container.querySelector('[data-total-price-modifier]');
    if (totalInput) totalInput.value = totalModifierDollars.toFixed(2);

    // Individual price tracking inputs — no name attribute, JS use only
    Object.entries(this.currentValues).forEach(([name, { priceModifier }]) => {
      const input = this.container.querySelector(`[data-price-input-for="${name}"]`);
      if (input) input.value = (parseFloat(priceModifier) || 0).toFixed(2);
    });

    this._updatePriceDisplay(totalCents);
    this._updateShipTime();

    const shipMinInput = this.container.querySelector('[data-ship-time-min]');
    const shipMaxInput = this.container.querySelector('[data-ship-time-max]');
    const specialOrder = this.container.querySelector('[data-special-order]');
    const returnable   = this.container.querySelector('[data-returnable]');

    const changedDetail = {
      hasShipTime:        this._hasModifierShipTime || false,
      shipTimeMin:        parseInt(shipMinInput?.value, 10) || 0,
      shipTimeMax:        parseInt(shipMaxInput?.value, 10) || 0,
      totalPriceModifier: totalModifierDollars,
      totalPriceCents:    totalCents,
      isSpecialOrder:     specialOrder?.value === 'true',
      isReturnable:       returnable?.value !== 'false',
      currentValues:      { ...this.currentValues },
    };
    console.log('[PropertyModifiers] dispatching modifiers:changed', changedDetail);
    document.dispatchEvent(new CustomEvent('modifiers:changed', { detail: changedDetail }));
  }

  _updatePriceDisplay(priceInCents) {
    const formatted = this._formatPresentmentCents(priceInCents);
    const section =
      this.container.closest('[data-section-type]') ||
      this.container.closest('.shopify-section') ||
      this.container.closest('section') ||
      document;

    section.querySelectorAll('[data-regular-price]').forEach(el => {
      if (el.closest('s')) return;
      el.textContent = formatted;
    });
  }

  // ─── Oversold (quantity exceeds on-hand stock) ─────────────────────────────
  //
  // Mirrors the non-customizable feature in stock-ship-estimate.js, but only for
  // a customizable product ordered with NO customization selected. Once any
  // modifier is chosen the line is made-to-order with its own lead time, so the
  // oversold path is intentionally skipped for it.

  // Re-evaluate ship time whenever the quantity changes. Listens on the whole
  // section because the quantity input lives in a different block than the
  // modifiers, so its bubbling 'change' event reaches the section, not the
  // modifiers container.
  _bindQuantityChange() {
    const section = this.container.closest('[data-section-id], [data-section-type], .shopify-section') || document;
    section.addEventListener('change', e => {
      if (e.target && e.target.matches && e.target.matches('input[name="quantity"]')) {
        this._updateShipTime();
      }
    });
  }

  // Requested quantity (qty box) plus this variant's count already in the cart,
  // matching the server-rendered data-cart-quantity used elsewhere.
  _requestedQtyTotal() {
    const section = this.container.closest('[data-section-id], [data-section-type], .shopify-section') || document;
    const input = section.querySelector(
      'quantity-input input[name="quantity"], input.quantity__input[name="quantity"], input[name="quantity"]'
    );
    const requested = input ? (parseInt(input.value, 10) || 1) : 1;
    const inCart = input ? (parseInt(input.getAttribute('data-cart-quantity'), 10) || 0) : 0;
    return requested + inCart;
  }

  // Stock attributes are already rendered on the shipping-estimate block by
  // product-information-blocks.liquid (data-inventory-*, data-restock-*-days).
  _readOversoldState() {
    const block = document.querySelector('[data-shipping-estimate]');
    if (!block) return null;
    const d = block.dataset;
    return {
      tracked:    d.inventoryTracked === 'true',
      policy:     d.inventoryPolicy,
      onHand:     parseInt(d.inventoryQty, 10),
      restockMin: parseInt(d.restockMinDays, 10) || 0,
      restockMax: parseInt(d.restockMaxDays, 10) || 0,
    };
  }

  _updateShipTime() {
    // ── Step 1: check for an exclusive modifier selection ──────────────────
    let exclusiveMin = null;
    let exclusiveMax = null;

    this.data.modifiers.forEach(modifier => {
      const name    = modifier.property_name;
      const current = this.currentValues[name];
      if (!current || !current.value) return;

      const wrapper = this.container.querySelector(`[data-property-name="${name}"]`);
      if (wrapper && wrapper.style.display === 'none') return;

      if (this.shipTimeData[name]?.isExclusive) {
        exclusiveMin = this.shipTimeData[name].min;
        exclusiveMax = this.shipTimeData[name].max;
      }
    });

    // ── Step 2: MAX of all non-exclusive modifiers with ship time ───────────
    let maxMin = 0;
    let maxMax = 0;
    let hasShipTime = false;

    if (exclusiveMin === null) {
      this.data.modifiers.forEach(modifier => {
        const name    = modifier.property_name;
        const current = this.currentValues[name];
        if (!current || !current.value) return;

        const wrapper = this.container.querySelector(`[data-property-name="${name}"]`);
        if (wrapper && wrapper.style.display === 'none') return;

        const min = this.shipTimeData[name]?.min || 0;
        const max = this.shipTimeData[name]?.max || 0;

        const modifierData   = this._getModifierData(name);
        const selectedOption = modifierData?.options.find(o => o.value === current.value);
        console.log('[ShipTime] inert check', name, current.value, '→', selectedOption?.ship_time_inert);
        if (selectedOption?.ship_time_inert) return;

        if (min > 0 || max > 0) {
          hasShipTime = true;
          if (min > maxMin) maxMin = min;
          if (max > maxMax) maxMax = max;
        }
      });
    } else {
      hasShipTime = true;
      maxMin = exclusiveMin;
      maxMax = exclusiveMax;
    }

    // ── Step 3: combine with out-of-stock base ──────────────────────────────
    // In stock + customizations → customization time replaces base entirely
    // Out of stock + customizations → add on top of base
    // Out of stock + no customizations → base time only
    //
    // Base is used when:
    //   a) never_in_stock product (made-to-order, always has lead time)
    //   b) regular product currently out of stock or on backorder
    //      detected via data-is-available="false" on the shipping estimate block

    const shippingBlock  = document.querySelector('[data-shipping-estimate]');
    const isAvailable    = shippingBlock?.dataset.isAvailable !== 'false';
    const isBackorder    = shippingBlock?.dataset.isBackorder === 'true';
    const blockBaseMin   = parseInt(shippingBlock?.dataset.baseMinDays, 10) || 0;
    const blockBaseMax   = parseInt(shippingBlock?.dataset.baseMaxDays, 10) || 0;

    // Oversold (plain): no customization selected AND the requested quantity
    // (plus what's already in cart) exceeds on-hand stock for a tracked,
    // backorder-allowed variant with a restock range. Treated like out-of-stock
    // below so the extra units carry the restock lead time. Customized lines are
    // made-to-order and never reach this branch.
    //
    // "Oversold" only applies when there is POSITIVE stock on hand — a genuine
    // "some now, the rest later" split. Two cases are deliberately excluded so
    // they fall through to the standard "Usually Ships: {range}" line instead:
    //   - onHand <= 0  → plain backorder. The inventory badge already says
    //     "Available on Backorder"; repeating it here (and surfacing a zero or
    //     negative count) is the duplication we're removing.
    //   - never_in_stock → Special Order. Same reasoning: the badge says
    //     "Special Order", so the ship line should not add a backorder message.
    const hasAnyCustomization = Object.values(this.currentValues).some(v => v.value !== '');
    const oversold = hasAnyCustomization ? null : this._readOversoldState();
    let isOversold = false;
    if (oversold && oversold.tracked && oversold.policy === 'continue' &&
        oversold.restockMin > 0 && !isNaN(oversold.onHand) &&
        oversold.onHand > 0 && !this.data.neverInStock &&
        this._requestedQtyTotal() > oversold.onHand) {
      isOversold = true;
    }
    this._oversoldOnHand = isOversold ? oversold.onHand : null;

    let baseMin, baseMax;
    if (this.data.neverInStock) {
      // never_in_stock: use metafield-driven base from JSON data block (existing logic preserved)
      baseMin = this.data.baseShipTimeMin || 0;
      baseMax = this.data.baseShipTimeMax || 0;
    } else if ((!isAvailable || isBackorder) && blockBaseMin > 0) {
      // Regular out-of-stock or backorder: use base days from shipping block
      baseMin = blockBaseMin;
      baseMax = blockBaseMax;
    } else if (isOversold) {
      // In stock but ordered beyond what's on hand: extra units ship in the
      // restock range. Reuses the out-of-stock combination logic below.
      baseMin = oversold.restockMin;
      baseMax = oversold.restockMax;
    } else {
      // In stock: no base — modifier time stands alone
      baseMin = 0;
      baseMax = 0;
    }

    let totalMin, totalMax;
    if (hasShipTime) {
      totalMin = baseMin + maxMin;
      totalMax = baseMax + maxMax;
    } else {
      totalMin = baseMin;
      totalMax = baseMax;
    }

    // ── Step 4: update hidden form inputs ──────────────────────────────────
    const shipMinInput = this.container.querySelector('[data-ship-time-min]');
    const shipMaxInput = this.container.querySelector('[data-ship-time-max]');
    if (shipMinInput) shipMinInput.value = totalMin > 0 ? totalMin : '';
    if (shipMaxInput) shipMaxInput.value = totalMax > 0 ? totalMax : '';

    // _special_order: true if any modifier with ship_time selected OR never_in_stock
    const specialOrderInput = this.container.querySelector('[data-special-order]');
    if (specialOrderInput) {
      specialOrderInput.value = (hasShipTime || this.data.neverInStock) ? 'true' : '';
    }

    // _returnable / Returns / Usually Ships
    const returnableInput = this.container.querySelector('[data-returnable]');
    const returnsNotice   = this.container.querySelector('[data-returns-notice]');
    const notReturnable   = hasShipTime || this.data.neverReturnable;

    if (returnableInput) returnableInput.value = notReturnable ? 'false' : 'true';
    if (returnsNotice) returnsNotice.value = notReturnable ? 'No' : '';

    // Store for modifiers:changed to read
    this._hasModifierShipTime = hasShipTime;

    // Sync name attributes — only inputs with meaningful values get submitted to cart
    this._syncSystemInputNames(hasShipTime, totalMin, totalMax, notReturnable);

    // ── Step 5: dispatch event for PDP shipping block display ───────────────
    this.container.dispatchEvent(new CustomEvent('modifiers:shiptime', {
      bubbles: true,
      detail: {
        hasModifierShipTime: hasShipTime,
        isExclusive:         exclusiveMin !== null,
        modifierMin:         maxMin,
        modifierMax:         maxMax,
        totalMin,
        totalMax,
        isOversold,
        oversoldOnHand:      this._oversoldOnHand,
      },
    }));
  }

  // ─── Sync system input name attributes ────────────────────────────────────
  //
  // Hidden system inputs have no name attribute in the DOM by default, so they
  // are never submitted on page load. This method adds or removes the name
  // attribute based on whether there is actually something worth reporting.
  //
  // Rules:
  //   _ship_time_min / _ship_time_max  → only when a modifier with ship time is selected
  //   _special_order                   → only when true (has ship time OR never_in_stock)
  //   _returnable                      → only when false (not returnable)
  //   Returns (visible)                → only when not returnable
  //   Usually Ships (visible)          → only when there is a non-standard ship time
  //   _total_price_modifier            → always has name (required by Shopify Function)

  _syncSystemInputNames(hasShipTime, totalMin, totalMax, notReturnable) {
    // Derive the form id from the total price modifier input which always has
    // the correct form attribute set directly in Liquid — more reliable than
    // this.form?.id which can resolve to the wrong element in some contexts.
    const totalPriceInput = this.container.querySelector('[data-total-price-modifier]');
    const rawFormAttr = totalPriceInput?.getAttribute('form');
    const formId = rawFormAttr || this.form?.id;
    console.log('[SystemInputs] totalPriceInput form attr raw:', rawFormAttr, '| type:', typeof rawFormAttr);
    console.log('[SystemInputs] this.form?.id:', this.form?.id, '| this.form tagName:', this.form?.tagName);

    const setName = (el, name, condition) => {
      if (!el) return;
      if (condition) {
        el.setAttribute('name', name);
        if (formId) el.setAttribute('form', formId);
      } else {
        el.removeAttribute('name');
      }
    };

    const shipMinInput  = this.container.querySelector('[data-ship-time-min]');
    const shipMaxInput  = this.container.querySelector('[data-ship-time-max]');
    const specialOrder  = this.container.querySelector('[data-special-order]');
    const returnable    = this.container.querySelector('[data-returnable]');
    const returnsNotice = this.container.querySelector('[data-returns-notice]');
    const shipTimeLabel = this.container.querySelector('[data-ship-time-label]');

    // Special order if: modifier with ship time selected, never_in_stock product,
    // OR any customization at all is selected (per client requirement)
    const hasAnyCustomization = Object.values(this.currentValues).some(v => v.value !== '');
    const isSpecialOrder = hasShipTime || this.data.neverInStock ;

    // Compute and set the ship label here so value and name are assigned together.
    // This avoids the race condition where _syncSystemInputNames runs before the
    // modifiers:shiptime listener has updated the input's value.
    //
    // Use base ship time only when the product actually needs it:
    //   - never_in_stock products always have a base lead time
    //   - out-of-stock / backorder products add base to modifier time
    //   - in-stock products with no modifiers → Next Business Day
    const shippingBlockEl   = document.querySelector('[data-shipping-estimate]');
    const labelIsAvailable  = shippingBlockEl?.dataset.isAvailable !== 'false';
    const labelIsBackorder  = shippingBlockEl?.dataset.isBackorder === 'true';
    const needsBase         = this.data.neverInStock || !labelIsAvailable || labelIsBackorder;

    if (shipTimeLabel) {
      let shipLabel;
      if (totalMin > 0) {
        shipLabel = _formatShipDays(totalMin, totalMax);
      } else if (needsBase && this.data.baseShipTimeMin > 0) {
        shipLabel = _formatShipDays(this.data.baseShipTimeMin, this.data.baseShipTimeMax);
      } else {
        shipLabel = 'Next Business Day';
      }
      shipTimeLabel.value = shipLabel;
      console.log('[SystemInputs] shipTimeLabel el:', shipTimeLabel);
      console.log('[SystemInputs] shipTimeLabel value set to:', shipLabel);
      console.log('[SystemInputs] shipTimeLabel form attr:', shipTimeLabel.getAttribute('form'));
    } else {
      console.warn('[SystemInputs] shipTimeLabel element NOT FOUND in container');
    }

    console.log('[SystemInputs] hasShipTime:', hasShipTime, '| totalMin:', totalMin, '| totalMax:', totalMax);
    console.log('[SystemInputs] isSpecialOrder:', isSpecialOrder, '| notReturnable:', notReturnable);
    console.log('[SystemInputs] neverInStock:', this.data.neverInStock, '| baseShipTimeMin:', this.data.baseShipTimeMin);

    // Visible "Special Order: Yes" property — shown to customer in cart/checkout
    // Managed dynamically: create/remove a hidden input as needed
    let visibleSpecialOrder = this.container.querySelector('[data-visible-special-order]');
    if (isSpecialOrder) {
      if (!visibleSpecialOrder) {
        visibleSpecialOrder = document.createElement('input');
        visibleSpecialOrder.type = 'hidden';
        visibleSpecialOrder.setAttribute('data-visible-special-order', '');
        visibleSpecialOrder.value = 'Yes';
        this.container.appendChild(visibleSpecialOrder);
      }
      visibleSpecialOrder.setAttribute('name', 'properties[Special Order]');
      if (formId) visibleSpecialOrder.setAttribute('form', formId);
    } else if (visibleSpecialOrder) {
      visibleSpecialOrder.removeAttribute('name');
    }

    setName(shipMinInput,  'properties[_ship_time_min]',  hasShipTime && totalMin > 0);
    setName(shipMaxInput,  'properties[_ship_time_max]',  hasShipTime && totalMax > 0);
    setName(specialOrder,  'properties[_special_order]',  isSpecialOrder);
    setName(returnable,    'properties[_returnable]',     notReturnable);
    setName(returnsNotice, 'properties[Returnable]',      notReturnable);
    setName(shipTimeLabel, 'properties[Usually Ships]',   !!shipTimeLabel?.value);

    console.log('[SystemInputs] Usually Ships name attr after setName:', shipTimeLabel?.getAttribute('name'));
    console.log('[SystemInputs] Usually Ships value after setName:', shipTimeLabel?.value);
    console.log('[SystemInputs] form id used:', formId, '| form el:', this.form?.tagName);
  }

  // ── is_exclusive: disable/enable all other modifier inputs ─────────────────

  _applyExclusiveLock() {
    let exclusiveName = null;

    this.data.modifiers.forEach(modifier => {
      const name    = modifier.property_name;
      const current = this.currentValues[name];
      if (!current || !current.value) return;

      const wrapper = this.container.querySelector(`[data-property-name="${name}"]`);
      if (wrapper && wrapper.style.display === 'none') return;

      if (this.shipTimeData[name]?.isExclusive) {
        exclusiveName = name;
      }
    });

    this.data.modifiers.forEach(modifier => {
      const name    = modifier.property_name;
      const wrapper = this.container.querySelector(
        `.property-modifier-field[data-property-name="${name}"]`
      );
      if (!wrapper) return;

      const isExclusiveItself  = name === exclusiveName;
      const isExclusiveParent  = exclusiveName &&
        this.data.modifiers.find(m => m.property_name === exclusiveName)?.conditional_on === name;

      if (exclusiveName && !isExclusiveItself && !isExclusiveParent) {
        const wasActive = this.currentValues[name]?.value;
        this.currentValues[name] = { value: '', priceModifier: 0 };

        wrapper.querySelectorAll('input, select, textarea').forEach(el => {
          el.disabled = true;
          if (el.type === 'checkbox') {
            el.checked = false;
          } else if (el.type === 'radio') {
            el.checked = false;
          } else if (!el.dataset.priceInputFor && !el.dataset.totalPriceModifier) {
            el.value = '';
          }
        });

        const priceInput = this.container.querySelector(`[data-price-input-for="${name}"]`);
        if (priceInput) priceInput.value = '0.00';

        const triggerLabel = this.container.querySelector(`[data-trigger-label="${name}"]`);
        if (triggerLabel) triggerLabel.textContent = 'None Selected';
        const triggerPrice = this.container.querySelector(`[data-trigger-price="${name}"]`);
        if (triggerPrice) triggerPrice.textContent = '';

        wrapper.classList.add('property-modifier-field--locked');
      } else {
        if (wrapper.style.display !== 'none') {
          wrapper.querySelectorAll('input, select, textarea').forEach(el => {
            el.disabled = false;
          });
        }
        wrapper.classList.remove('property-modifier-field--locked');
      }
    });
  }

  // ─── Money formatting (presentment-aware) ──────────────────────────────────
  //
  // price_modifier values come from the metaobject in the shop's base currency
  // (USD). For display we convert them to the buyer's presentment currency using
  // the Shopify Markets rate, then format with Intl so zero-decimal currencies
  // (e.g. JPY) and thousands separators are handled correctly.

  // Write converted, currency-formatted prices into the option labels. Prices
  // are rendered here rather than in Liquid because only the client knows the
  // Markets conversion rate (via Shopify.currency).
  _renderOptionPrices() {
    // Native <select> options (visual-select hidden clones are skipped)
    this.container
      .querySelectorAll('select[data-property-type]:not([data-visual-select]) option[data-price-modifier]')
      .forEach(opt => {
        const pm   = parseFloat(opt.dataset.priceModifier) || 0;
        const base = opt.dataset.optionLabel != null
          ? opt.dataset.optionLabel
          : opt.textContent.trim();
        opt.dataset.optionLabel = base;
        const price = this._formatModifierPrice(pm);
        opt.textContent = price ? `${base} (${price})` : base;
      });

    // Visual selector tiles
    this.container
      .querySelectorAll('.visual-selector__tile[data-price-modifier]')
      .forEach(tile => {
        const priceEl = tile.querySelector('.visual-selector__tile-price');
        if (!priceEl) return;
        const pm = parseFloat(tile.dataset.priceModifier) || 0;
        priceEl.textContent = this._formatModifierPrice(pm);
      });

    // Boolean checkbox prices (value taken from the JSON data block)
    this.data.modifiers.forEach(modifier => {
      if (modifier.type !== 'boolean') return;
      const trueOption = (modifier.options || []).find(o =>
        ['true', 'yes'].includes((o.value || '').toLowerCase())
      );
      const pm   = parseFloat(trueOption && trueOption.price_modifier) || 0;
      const span = this.container.querySelector(`[data-bool-price="${modifier.property_name}"]`);
      if (span) {
        const price = this._formatModifierPrice(pm);
        span.textContent = price ? ` (${price})` : '';
      }
    });
  }

  // Format a signed USD price_modifier (e.g. 50 or -10) as a converted,
  // currency-formatted string with a leading + / - (empty string for 0).
  _formatModifierPrice(usdAmount) {
    const value = parseFloat(usdAmount) || 0;
    if (value === 0) return '';
    const sign  = value > 0 ? '+' : '-';
    const cents = Math.round(Math.abs(value) * this.presentmentRate * 100);
    return sign + this._formatPresentmentCents(cents);
  }

  // Format an integer amount already in presentment-currency "cents"
  // (base unit × 100, matching Shopify's variant.price convention).
  _formatPresentmentCents(cents) {
    const amount = (parseFloat(cents) || 0) / 100;
    if (this.presentmentCurrency && typeof Intl !== 'undefined' && Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.presentmentCurrency,
        }).format(amount);
      } catch (e) {
        // Unknown currency code — fall back to the shop money format below.
      }
    }
    return this._formatMoney(cents, this.data.currencyFormat);
  }

  // Fallback formatter using the shop's Liquid money_format string. Handles all
  // standard Shopify amount placeholders so no raw {{...}} token can leak through.
  _formatMoney(cents, format) {
    const fmt   = format || '${{amount}}';
    const value = (parseFloat(cents) || 0) / 100;

    const group = (intStr, sep) => intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const build = (decimals, thousands, decimalMark) => {
      const fixed = Math.abs(value).toFixed(decimals);
      const parts = fixed.split('.');
      const body  = parts[1]
        ? group(parts[0], thousands) + decimalMark + parts[1]
        : group(parts[0], thousands);
      return value < 0 ? '-' + body : body;
    };

    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g,                                  build(2, ',', '.'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g,                      build(0, ',', '.'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g,             build(2, '.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, build(0, '.', ','))
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g,        build(2, "'", '.'))
      .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g,             build(2, ' ', ','))
      .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, build(0, ' ', ' '));
  }

  // ─── Form validation ───────────────────────────────────────────────────────

  _bindFormValidation() {
    if (!this.form) return;

    this.container.addEventListener('change', () => this._updateAtcState());
    this.container.addEventListener('input', () => this._updateAtcState());

    this.container.addEventListener('blur', e => {
      const input = e.target;
      if (!input.name && !input.dataset.propertyName) return;
      const key = input.name || input.dataset.propertyName;
      this.touchedFields.add(key);
      this._updateAtcState();
    }, true);

    const atcButton = this.form.querySelector('[name="add"]');
    if (atcButton) {
      const overlay = document.createElement('div');
      overlay.setAttribute('data-modifier-atc-overlay', '');
      overlay.style.cssText = 'position:absolute;inset:0;z-index:10;cursor:not-allowed;display:none;';
      atcButton.style.position = 'relative';
      atcButton.appendChild(overlay);

      overlay.addEventListener('click', () => {
        const incomplete = this._getIncompleteFields();
        incomplete.forEach(f => {
          if (f.type === 'modifier') this.touchedFields.add(f.name);
          if (f.type === 'conditional-text') this.touchedFields.add(f.input.name);
        });
        this._updateAtcState();
        const first = this.container.querySelector('.property-modifier-field--incomplete');
        if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      this._atcOverlay = overlay;
    }

    this._updateAtcState();
  }

  _updateAtcState() {
    const atcButton = this.form.querySelector('[name="add"]');
    if (!atcButton) return;

    const incomplete = this._getIncompleteFields();

    if (incomplete.length > 0) {
      atcButton.setAttribute('disabled', 'disabled');
      atcButton.dataset.modifierBlocked = 'true';
      if (this._atcOverlay) this._atcOverlay.style.display = 'block';
      const touchedIncomplete = incomplete.filter(f => {
        if (f.type === 'modifier') return this.touchedFields.has(f.name);
        if (f.type === 'conditional-text') return this.touchedFields.has(f.input.name);
        return false;
      });
      if (touchedIncomplete.length > 0) {
        this._showAtcMessage(touchedIncomplete);
        this._highlightIncompleteFields(touchedIncomplete);
      } else {
        this._clearAtcMessage();
        this._clearFieldHighlights();
      }
    } else {
      if (atcButton.dataset.modifierBlocked === 'true') {
        atcButton.removeAttribute('disabled');
        delete atcButton.dataset.modifierBlocked;
      }
      if (this._atcOverlay) this._atcOverlay.style.display = 'none';
      this._clearAtcMessage();
      this._clearFieldHighlights();
    }
  }

  _showAtcMessage(incomplete) {
    let msg = this.form.querySelector('[data-modifier-atc-message]');
    if (!msg) {
      msg = document.createElement('p');
      msg.setAttribute('data-modifier-atc-message', '');
      msg.className = 'property-modifier-atc-message';
      const atcButton = this.form.querySelector('[name="add"]');
      atcButton.parentElement.insertBefore(msg, atcButton);
    }

    const fieldNames = incomplete.map(f => {
      if (f.type === 'modifier') {
        const mod = this.data.modifiers.find(m => m.property_name === f.name);
        return mod?.title || f.name;
      }
      if (f.type === 'conditional-text') {
        return f.input.closest('.property-conditional-field')
          ?.querySelector('label')?.textContent?.trim() || 'required field';
      }
      return 'required field';
    });

    const unique = [...new Set(fieldNames)];
    msg.textContent = unique.length === 1
      ? `Please complete: ${unique[0]}`
      : `Please complete: ${unique.slice(0, -1).join(', ')} and ${unique[unique.length - 1]}`;
  }

  _clearAtcMessage() {
    this.form.querySelector('[data-modifier-atc-message]')?.remove();
  }

  _highlightIncompleteFields(incomplete) {
    this._clearFieldHighlights();

    incomplete.forEach(f => {
      if (f.type === 'modifier') {
        const wrapper = this.container.querySelector(
          `.property-modifier-field[data-property-name="${f.name}"]`
        );
        wrapper?.classList.add('property-modifier-field--incomplete');
      }
      if (f.type === 'conditional-text') {
        f.input.closest('.property-conditional-field')
          ?.classList.add('property-modifier-field--incomplete');
      }
    });
  }

  _clearFieldHighlights() {
    this.container.querySelectorAll('.property-modifier-field--incomplete')
      .forEach(el => el.classList.remove('property-modifier-field--incomplete'));
  }

  _getIncompleteFields() {
    const incomplete = [];

    this.data.modifiers.forEach(modifier => {
      if (!modifier.required) return;

      const wrapper = this.container.querySelector(
        `[data-property-name="${modifier.property_name}"]`
      );
      if (wrapper?.style.display === 'none') return;

      const value = this.currentValues[modifier.property_name]?.value;
      if (!value || value === '') {
        incomplete.push({ type: 'modifier', name: modifier.property_name });
      }
    });

    this.container
      .querySelectorAll('.property-conditional-field[data-trigger-type="text"]')
      .forEach(wrapper => {
        if (wrapper.style.display === 'none') return;

        const parentName = wrapper.dataset.conditionalFor;
        const parentWrapper = this.container.querySelector(
          `.property-modifier-field[data-property-name="${parentName}"]`
        );
        if (parentWrapper?.style.display === 'none') return;

        const input = wrapper.querySelector('input[data-property-type="conditional-text"]');
        if (!input || input.disabled) return;

        if (!input.value || input.value.trim() === '') {
          incomplete.push({ type: 'conditional-text', input });
        }
      });

    return incomplete;
  }

  _validateAll() {
    const incomplete = this._getIncompleteFields();

    incomplete.forEach(({ type, name, input, wrapper }) => {
      if (type === 'modifier') this._showFieldError(name);
      if (type === 'conditional-text') this._showConditionalFieldError(
        input.closest('.property-conditional-field'), input
      );
    });

    return incomplete.length === 0;
  }

  _showConditionalFieldError(wrapper, input) {
    wrapper.classList.add('property-modifier-field--error');
    input.classList.add('property-modifier-field--error');
    input.focus();
    input.addEventListener('input', () => {
      wrapper.classList.remove('property-modifier-field--error');
      input.classList.remove('property-modifier-field--error');
    }, { once: true });
  }

  _showFieldError(propertyName) {
    const wrapper = this.container.querySelector(
      `.property-modifier-field[data-property-name="${propertyName}"]`
    );
    if (!wrapper) return;
    wrapper.classList.add('property-modifier-field--error');
    wrapper.addEventListener('change', () => {
      wrapper.classList.remove('property-modifier-field--error');
    }, { once: true });
  }

  // ─── Variant change ────────────────────────────────────────────────────────

  _bindVariantChange() {
    document.addEventListener('variant:changed', e => {
      if (!this.container.closest('[data-section-id]')) return;
      const sectionId = this.container.closest('[data-section-id]').dataset.sectionId;
      if (e.detail?.sectionId && e.detail.sectionId !== sectionId) return;

      if (e.detail?.variant?.price !== undefined) {
        this.data.basePrice = e.detail.variant.price;
        this._updateTotalPrice();
      }
    });
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  _getModifierData(propertyName) {
    return this.data.modifiers.find(m => m.property_name === propertyName) || null;
  }
}

// ─── Initialisation ─────────────────────────────────────────────────────────

function initPropertyModifiers(root = document) {
  root.querySelectorAll('[data-property-modifiers]').forEach(container => {
    if (!container._propertyModifiers) {
      container._propertyModifiers = new PropertyModifiers(container);
    }
  });
}

document.addEventListener('DOMContentLoaded', () => initPropertyModifiers());

document.addEventListener('shopify:section:load', e => {
  initPropertyModifiers(e.target);
});

// ─── Shipping estimate block updater ───────────────────────────────────────

document.addEventListener('modifiers:shiptime', e => {
  const { totalMin, totalMax, hasModifierShipTime, isOversold, oversoldOnHand } = e.detail;

  const section = e.target.closest('[data-section-type], .shopify-section');
  if (!section) return;

  const block = section.querySelector('[data-shipping-estimate]');
  if (!block) return;

  const rangeEl     = block.querySelector('[data-ship-estimate-range]');
  if (!rangeEl) return;
  const labelEl     = block.querySelector('[data-ship-estimate-label]');

  // Oversold (plain, no customization): mirror the non-customizable message and
  // hide the "Usually ships" prefix. The stored properties[Usually Ships] is the
  // clean range, set in _syncSystemInputNames.
  if (isOversold) {
    const range = _formatShipDays(totalMin, totalMax);
    if (labelEl) labelEl.hidden = true;
    rangeEl.textContent = 'Only ' + oversoldOnHand + ' in stock. Additional units ship in ' + range + '.';
    return;
  }

  if (labelEl) labelEl.hidden = false;

  const baseMin     = parseInt(block.dataset.baseMinDays, 10) || 0;
  const baseMax     = parseInt(block.dataset.baseMaxDays, 10) || 0;

  let shipLabel;
  if (hasModifierShipTime) {
    shipLabel = _formatShipDays(totalMin, totalMax);
  } else if (baseMin > 0) {
    shipLabel = _formatShipDays(baseMin, baseMax);
  } else {
    shipLabel = 'Next Business Day';
  }

  rangeEl.textContent = shipLabel;

  // shipTimeLabel value and name are now managed in _syncSystemInputNames
});

function _formatShipDays(min, max) {
  if (min >= 14) {
    const minWeeks = Math.ceil(min / 7);
    const maxWeeks = Math.ceil(max / 7);
    return minWeeks === maxWeeks
      ? `${minWeeks} week${minWeeks !== 1 ? 's' : ''}`
      : `${minWeeks}–${maxWeeks} weeks`;
  }
  return min === max ? `${min} day${min !== 1 ? 's' : ''}` : `${min}–${max} days`;
}


// ─── Conditional Alert ─────────────────────────────────────────────────────

document.addEventListener('modifiers:changed', e => {
  const { hasShipTime } = e.detail;
  console.log('[ConditionalAlert] modifiers:changed received', { hasShipTime });

  const alertBlocks = document.querySelectorAll('[data-conditional-alert][data-dynamic="true"]');
  console.log('[ConditionalAlert] dynamic alert blocks found:', alertBlocks.length);

  alertBlocks.forEach(block => {
    const triggerOnShipTime = block.dataset.triggerOnModifierShipTime === 'true';
    const staticMatch       = block.dataset.staticMatch === 'true';
    console.log('[ConditionalAlert] evaluating block', { triggerOnShipTime, staticMatch, hasShipTime });

    const showMatch = staticMatch || (triggerOnShipTime && hasShipTime);

    const matchEl = block.querySelector('[data-alert-state="match"]');
    const elseEl  = block.querySelector('[data-alert-state="else"]');

    if (matchEl) matchEl.style.display = showMatch ? '' : 'none';
    if (elseEl)  elseEl.style.display  = showMatch ? 'none' : '';
  });
});